import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from '@/lib/i18n'
import { BrandPage, BrandRow } from '@/routes/-components/brand-canvas'

/**
 * The native apps' claimed https return address
 * (`${frontend.user.baseUrl}/app/auth/callback`). On a phone with the app
 * installed the OS hands the link to the app's sign-in sheet and this page is
 * never drawn; it is what a person sees when that does not happen (app not
 * installed, link opened somewhere else). It says so and nothing more.
 *
 * **The query is never read and never leaves the browser.** It may carry a
 * one-time `code`, so there is no `validateSearch`, no loader and no link on
 * the page, and the head tells the browser to send no `Referer`: the
 * branding read this page makes is same-origin, and under the default policy
 * a same-origin request carries the full address, query included, into the
 * proxy's logs. `noindex` keeps the address out of a crawler's hands too. Sentry, when a
 * DSN is set, scrubs this path's query from everything it sends
 * (`@/lib/scrub-callback-query`).
 *
 * **Outside `(signed-out)` on purpose.** That group sends a signed-in browser
 * home; someone who opened this link from the browser they are signed in with
 * must still be told to go back to the app. `ssr: false` because the address
 * is a credential carrier and a server render would pass it through one more
 * process for a page that has nothing to say about it.
 */
export const Route = createFileRoute('/app/auth/callback/')({
	ssr: false,
	head: () => ({
		meta: [
			{ name: 'referrer', content: 'no-referrer' },
			{ name: 'robots', content: 'noindex, nofollow' }
		]
	}),
	component: NativeReturnPage
})

function NativeReturnPage() {
	const { t } = useTranslation()

	return (
		<BrandPage>
			<BrandRow />
			<h1 className='font-semibold text-2xl tracking-tight'>
				{t('client:native_return.title')}
			</h1>
			<p className='mt-2 text-muted-foreground text-sm'>
				{t('client:native_return.body')}
			</p>
		</BrandPage>
	)
}
